import { z } from 'zod';
import { partnerSchema, type Country, type Partner, type PartnerErpValues } from '../aggregated';

export type GetPartnerHierarchyProps = {
  partnerId: string;
  allDescendants: boolean;
  includeAncestors: boolean;
  filterPartners?: string[];
  compressed?: boolean;
};

export type PartnerTreeNode = {
  item: PartnerResponse;
  children: PartnerTreeNode[];
};

export const PartnerSlimSchema = partnerSchema.partial().and(
  partnerSchema.pick({
    sgtId: true,
    id: true,
    dwKey: true,
    name: true,
    parentId: true,
    childCount: true,
    locationCount: true,
    customerText: true,
    additionalDescription: true,
    street: true,
    city: true,
    countryId: true,
    companyId: true,
    isLocation: true,
    hasActiveChildren: true,
    source: true,
    erp: true,
    billable: true,
    description: true,
    isActive: true,
    isActivePickupPlace: true,
  }),
);

export type PartnerSlim = z.infer<typeof PartnerSlimSchema>;

export const PartnerTreeNodeV2 = z.object({
  item: partnerSchema.or(PartnerSlimSchema),
  get children() {
    return z.array(PartnerTreeNodeV2);
  },
});

export type PartnerTreeNodeV2 = z.infer<typeof PartnerTreeNodeV2>;

export type PartnerResItem = Partner | PartnerSlim;

export type PartnerResponse = {
  dwKey: string;
  id: string;
  city: string;
  companyId: Country;
  countryId: string;
  name: string;
  description: string;
  street: string;
  customerDescription: string;
  customerText: string;
  parentId?: string;
  additionalDescription: string;
  postalCode: string;
  isActive: boolean;
  isActivePickupPlace: boolean;
  childCount: number;
  locationCount: number;
  pickupAddressId?: string;
  source: string;
  /** The business system this Partner transacts against, resolved by the partner API. */
  erp?: PartnerErpValues;
  hasActiveChildren: boolean | undefined;
  billable: boolean;
  isPriceAgreed: boolean;
  isLocation?: boolean;
  isCS?: boolean;
  singleDeliveryAddress?: boolean;
};

export type DistinctHierarchy = {
  dwKeys: string[];
  rootId: string;
};

export type DistinctHierarchiesResponse = {
  availableHierarchies: DistinctHierarchy[];
};

export type LocationsById = Record<string, PartnerResItem | undefined>;
